import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  Dimensions,
  ActivityIndicator,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import LoginMapPanel from '../components/LoginMapPanel';
import { hasSuperUserAccess } from '../utils/sessionUser';

const CHART_COLORS = [
  '#0176d3',
  '#2e844a',
  '#ba0517',
  '#5c4ee5',
  '#e07f13',
  '#032d60',
  '#8a6a00',
  '#706e6b',
];

function useWindowWidth() {
  const [w, setW] = useState(Dimensions.get('window').width);
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => {
      setW(window.width);
    });
    return () => sub?.remove?.();
  }, []);
  return w;
}

function DonutVisual({ segments, theme, isDark }) {
  const total = segments.reduce((a, s) => a + (s.count || 0), 0) || 1;
  let deg = 0;
  const parts = [];
  segments.forEach((s, i) => {
    const pct = ((s.count || 0) / total) * 360;
    const start = deg;
    deg += pct;
    const c = CHART_COLORS[i % CHART_COLORS.length];
    parts.push(`${c} ${start}deg ${deg}deg`);
  });
  const gradient = parts.join(', ') || `${CHART_COLORS[0]} 0deg 360deg`;
  const hole = isDark ? theme.colors.surface : '#ffffff';

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.donutWrap, { width: 120, height: 120 }]}>
        <View
          style={{
            position: 'absolute',
            width: 120,
            height: 120,
            borderRadius: 60,
            background: `conic-gradient(${gradient})`,
          }}
        />
        <View
          style={{
            width: 72,
            height: 72,
            borderRadius: 36,
            backgroundColor: hole,
            zIndex: 1,
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.donutFallback}>
      {segments.map((s, i) => (
        <View key={i} style={styles.legendRow}>
          <View
            style={[
              styles.legendSwatch,
              { backgroundColor: CHART_COLORS[i % CHART_COLORS.length] },
            ]}
          />
          <Text style={[styles.legendText, { color: theme.colors.text }]}>
            {s.device_type || s.os || '—'} · {s.count} (
            {Math.round(((s.count || 0) / total) * 100)}%)
          </Text>
        </View>
      ))}
    </View>
  );
}

function HorizontalBars({ rows, labelKey, theme, isDark }) {
  const max = Math.max(1, ...rows.map((r) => r.count || 0));
  return (
    <View style={styles.barBlock}>
      {rows.length === 0 ? (
        <Text style={[styles.emptyHint, { color: theme.colors.text }]}>
          No data for this period.
        </Text>
      ) : (
        rows.map((row, i) => {
          const pct = ((row.count || 0) / max) * 100;
          const label = row[labelKey] || row.student_name || '—';
          return (
            <View key={`${label}-${i}`} style={styles.hBarRow}>
              <Text
                style={[styles.hBarLabel, { color: theme.colors.text }]}
                numberOfLines={1}
              >
                {label}
              </Text>
              <View
                style={[
                  styles.hBarTrack,
                  { backgroundColor: isDark ? '#3e3e3c' : '#e5e5e5', width: '100%' },
                ]}
              >
                <View
                  style={[
                    styles.hBarFill,
                    {
                      width: `${pct}%`,
                      backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                    },
                  ]}
                />
              </View>
              <Text style={[styles.hBarCount, { color: theme.colors.text }]}>
                {row.count}
              </Text>
            </View>
          );
        })
      )}
    </View>
  );
}

function VerticalBars({ rows, theme, isDark }) {
  const max = Math.max(1, ...rows.map((r) => r.count || 0));
  return (
    <View style={styles.vChart}>
      <View style={styles.vBarsRow}>
        {rows.length === 0 ? (
          <Text style={[styles.emptyHint, { color: theme.colors.text }]}>
            No logins recorded today yet.
          </Text>
        ) : (
          rows.map((row, i) => {
            const h = ((row.count || 0) / max) * 120;
            return (
              <View key={row.user_id || i} style={styles.vCol}>
                <View
                  style={[
                    styles.vBar,
                    {
                      height: Math.max(4, h),
                      backgroundColor:
                        CHART_COLORS[i % CHART_COLORS.length],
                    },
                  ]}
                />
                <Text
                  style={[styles.vLabel, { color: theme.colors.text }]}
                  numberOfLines={2}
                >
                  {row.student_name}
                </Text>
                <Text style={[styles.vCount, { color: theme.colors.heading }]}>
                  {row.count}
                </Text>
              </View>
            );
          })
        )}
      </View>
    </View>
  );
}

function DataTable({ columns, rows, theme, isDark }) {
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator>
      <View>
        <View style={[styles.tr, styles.trHead, { borderColor: isDark ? '#444' : '#c9c9c9' }]}>
          {columns.map((c) => (
            <Text
              key={c.key}
              style={[
                styles.th,
                { width: c.width || 120, color: theme.colors.heading },
              ]}
            >
              {c.label}
            </Text>
          ))}
        </View>
        {rows.length === 0 ? (
          <Text style={[styles.emptyHint, { color: theme.colors.text, padding: 12 }]}>
            No rows.
          </Text>
        ) : (
          rows.map((row, ri) => (
            <View
              key={ri}
              style={[
                styles.tr,
                {
                  backgroundColor:
                    ri % 2 === 0
                      ? isDark
                        ? 'rgba(255,255,255,0.03)'
                        : '#fafaf9'
                      : 'transparent',
                  borderColor: isDark ? '#333' : '#e5e5e5',
                },
              ]}
            >
              {columns.map((c) => (
                <Text
                  key={c.key}
                  style={[
                    styles.td,
                    { width: c.width || 120, color: theme.colors.text },
                  ]}
                  numberOfLines={c.multiline ? undefined : 2}
                >
                  {row[c.key] != null ? String(row[c.key]) : '—'}
                </Text>
              ))}
            </View>
          ))
        )}
      </View>
    </ScrollView>
  );
}

export default function UserAuditPage({ navigation, route }) {
  const theme = useTheme();
  const isDark = theme.isDarkMode;
  const stopwatchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [twoFaUsers, setTwoFaUsers] = useState([]);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaResettingId, setTwoFaResettingId] = useState(null);
  const [resetAllBusy, setResetAllBusy] = useState(false);
  const [toggleUsers, setToggleUsers] = useState([]);
  const [toggleUsersLoading, setToggleUsersLoading] = useState(false);
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const width = useWindowWidth();

  const isDesktop = width >= 1024;
  const isTablet = width >= 640 && width < 1024;

  const gridItemStyle = useMemo(
    () => ({
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: isDesktop ? '31%' : isTablet ? '47%' : '100%',
      minWidth: isDesktop ? 280 : isTablet ? 260 : '100%',
      maxWidth: '100%',
      padding: 8,
    }),
    [isDesktop, isTablet]
  );

  useEffect(() => {
    const u = route?.params?.user;
    if (u) setCurrentUser(u);
    if (u && !hasSuperUserAccess(u)) {
      navigation.replace('MainPage', { user: u });
    }
  }, [route?.params?.user, navigation]);

  const load = useCallback(async () => {
    const u = currentUser || route?.params?.user;
    if (!u?.id || !hasSuperUserAccess(u)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = `${getApiUrl()}/api/user-audit/dashboard?auditor_id=${encodeURIComponent(
        u.id
      )}`;
      const res = await fetch(url);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load audit data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser, route?.params?.user]);

  useEffect(() => {
    load();
  }, [load]);

  const loadTwoFaUsers = useCallback(async () => {
    const u = currentUser || route?.params?.user;
    if (!hasSuperUserAccess(u)) return;
    setTwoFaLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/users?requester_id=${encodeURIComponent(String(u.id))}`
      );
      const list = await res.json().catch(() => []);
      if (!res.ok) throw new Error(list.error || 'Failed to load users');
      const rows = (Array.isArray(list) ? list : []).filter(
        (row) => row.is_two_factor_user === true && row.super_user !== true
      );
      setTwoFaUsers(rows);
    } catch (e) {
      setTwoFaUsers([]);
    } finally {
      setTwoFaLoading(false);
    }
  }, [currentUser, route?.params?.user]);

  useEffect(() => {
    void loadTwoFaUsers();
  }, [loadTwoFaUsers]);

  const loadToggleUsers = useCallback(async () => {
    const u = currentUser || route?.params?.user;
    if (!hasSuperUserAccess(u)) return;
    setToggleUsersLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/users?requester_id=${encodeURIComponent(String(u.id))}`
      );
      const list = await res.json().catch(() => []);
      if (!res.ok) throw new Error(list.error || 'Failed to load users');
      const rows = (Array.isArray(list) ? list : [])
        .filter((row) => row.super_user !== true)
        .sort((a, b) =>
          String(a.student_name || a.username || '').localeCompare(
            String(b.student_name || b.username || ''),
            undefined,
            { sensitivity: 'base' }
          )
        );
      setToggleUsers(rows);
    } catch {
      setToggleUsers([]);
    } finally {
      setToggleUsersLoading(false);
    }
  }, [currentUser, route?.params?.user]);

  useEffect(() => {
    void loadToggleUsers();
  }, [loadToggleUsers]);

  const resetTwoFactorForUser = (target) => {
    const actor = currentUser || route?.params?.user;
    if (!actor?.id || !target?.id) return;
    Alert.alert(
      'Reset 2FA for user',
      `Clear authenticator, backup codes, and timestamps for ${target.student_name || target.username}? They must set up again on next login.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: () => void (async () => {
            setTwoFaResettingId(target.id);
            try {
              const res = await fetch(`${getApiUrl()}/api/auth/2fa/reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  actor_user_id: actor.id,
                  target_student_id: target.id,
                }),
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok) {
                Alert.alert('Reset failed', data.error || 'Could not reset user');
                return;
              }
              Alert.alert('Done', data.message || 'Two-factor reset for this user.');
              await loadTwoFaUsers();
            } catch (e) {
              Alert.alert('Error', e.message || 'Reset failed');
            } finally {
              setTwoFaResettingId(null);
            }
          })(),
        },
      ]
    );
  };

  const resetAllTwoFactorUsers = () => {
    const actor = currentUser || route?.params?.user;
    if (!actor?.id) return;
    Alert.alert(
      'Reset all 2FA users',
      `This clears enrollment for every student with two-factor required (${twoFaUsers.length} shown). Each must set up again on next login. Continue?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset all',
          style: 'destructive',
          onPress: () => void (async () => {
            setResetAllBusy(true);
            try {
              const res = await fetch(`${getApiUrl()}/api/auth/2fa/admin/reset-all`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ actor_user_id: actor.id }),
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok) {
                Alert.alert('Reset failed', data.error || 'Could not reset all users');
                return;
              }
              Alert.alert(
                'All reset',
                data.message ||
                  `Reset ${data.reset_count ?? 0} user(s).`
              );
              await loadTwoFaUsers();
            } catch (e) {
              Alert.alert('Error', e.message || 'Reset failed');
            } finally {
              setResetAllBusy(false);
            }
          })(),
        },
      ]
    );
  };

  const toggleTwoFactorRequired = (target) => {
    const actor = currentUser || route?.params?.user;
    if (!actor?.id || !target?.id || toggleBusyId != null) return;
    const nextEnabled = !Boolean(target.is_two_factor_user);

    void (async () => {
      setToggleBusyId(target.id);
      try {
        const res = await fetch(`${getApiUrl()}/api/auth/2fa/admin/set-required`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            actor_user_id: actor.id,
            target_student_id: target.id,
            is_two_factor_user: nextEnabled,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          Alert.alert('Update failed', data.error || 'Could not update two-factor setting');
          return;
        }
        setToggleUsers((prev) =>
          prev.map((row) =>
            row.id === target.id ? { ...row, is_two_factor_user: nextEnabled } : row
          )
        );
        await loadTwoFaUsers();
      } catch (e) {
        Alert.alert('Error', e.message || 'Update failed');
      } finally {
        setToggleBusyId(null);
      }
    })();
  };

  const pageBg = isDark ? '#181818' : '#f3f2f2';
  const bannerBg = isDark ? '#032d60' : '#032d60';
  const cardBg = isDark ? theme.colors.surface : '#ffffff';
  const cardBorder = isDark ? '#3e3e3c' : '#dddbda';

  const renderWidget = (title, subtitle, body) => (
    <View style={gridItemStyle} key={title}>
      <View
        style={[
          styles.widgetCard,
          { backgroundColor: cardBg, borderColor: cardBorder },
        ]}
      >
        <View style={styles.widgetHead}>
          <Text style={[styles.widgetTitle, { color: theme.colors.heading }]}>
            {title}
          </Text>
          {subtitle ? (
            <Text style={[styles.widgetSub, { color: theme.colors.text }]}>
              {subtitle}
            </Text>
          ) : null}
        </View>
        {body}
      </View>
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: pageBg }]} data-theme={isDark ? 'dark' : 'light'} {...(Platform.OS === 'web' ? { dataSet: { screen: 'user-audit' } } : {})}>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <View style={[styles.header, { backgroundColor: isDark ? theme.colors.background : '#fff' }]}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={[styles.breadcrumb, { color: theme.colors.heading }]}>
            User Audit
          </Text>
          <Text style={[styles.breadcrumbSub, { color: theme.colors.text }]}>
            Trusted services · Login intelligence
          </Text>
        </View>
        <ProfileIcon navigation={navigation} user={currentUser} />
      </View>

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <TouchableOpacity onPress={load} style={styles.refreshBtn} activeOpacity={0.7}>
          <Text style={[styles.refreshBtnText, { color: '#0176d3' }]}>Refresh data</Text>
        </TouchableOpacity>

        {loading && (
          <ActivityIndicator size="large" color="#0176d3" style={{ marginVertical: 24 }} />
        )}
        {error && (
          <View style={[styles.errorBox, { borderColor: '#ba0517' }]}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        <View
          style={[
            styles.twoFaAdminCard,
            { backgroundColor: cardBg, borderColor: cardBorder },
          ]}
        >
          <Text style={[styles.twoFaAdminTitle, { color: theme.colors.heading }]}>
            Two-factor reset (admin)
          </Text>
          <Text style={[styles.twoFaAdminSub, { color: theme.colors.text }]}>
            Clears encrypted secrets, enabled timestamps, backup codes, and OTP replay rows. Users
            stay on 2FA and must enroll again on next login.
          </Text>
          <TouchableOpacity
            style={[styles.twoFaResetAllBtn, resetAllBusy && { opacity: 0.6 }]}
            onPress={resetAllTwoFactorUsers}
            disabled={resetAllBusy || twoFaLoading}
            activeOpacity={0.85}
          >
            {resetAllBusy ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.twoFaResetAllBtnText}>Reset all 2FA users</Text>
            )}
          </TouchableOpacity>
          {twoFaLoading ? (
            <ActivityIndicator style={{ marginTop: 12 }} color="#0176d3" />
          ) : (
            <View style={styles.twoFaUserList}>
              {twoFaUsers.map((row) => {
                const enabled = Boolean(row.two_factor_enabled_at);
                const busy = twoFaResettingId === row.id;
                return (
                  <View
                    key={row.id}
                    style={[styles.twoFaUserRow, { borderColor: cardBorder }]}
                  >
                    <View style={styles.twoFaUserInfo}>
                      <Text style={[styles.twoFaUserName, { color: theme.colors.text }]}>
                        {row.student_name || row.username}
                      </Text>
                      <Text style={[styles.twoFaUserMeta, { color: theme.colors.text }]}>
                        {enabled ? '2FA enabled' : 'Setup pending / not enabled'}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={[styles.twoFaUserResetBtn, busy && { opacity: 0.6 }]}
                      onPress={() => resetTwoFactorForUser(row)}
                      disabled={busy || resetAllBusy}
                    >
                      {busy ? (
                        <ActivityIndicator size="small" color="#fff" />
                      ) : (
                        <Text style={styles.twoFaUserResetBtnText}>Reset</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                );
              })}
              {!twoFaUsers.length && !twoFaLoading ? (
                <Text style={[styles.twoFaEmpty, { color: theme.colors.text }]}>
                  No two-factor students found.
                </Text>
              ) : null}
            </View>
          )}

          <View style={[styles.twoFaSectionDivider, { borderColor: cardBorder }]} />

          <Text style={[styles.twoFaAdminTitle, { color: theme.colors.heading, marginTop: 4 }]}>
            Toggle 2-Factor Login
          </Text>
          <Text style={[styles.twoFaAdminSub, { color: theme.colors.text }]}>
            Turn two-factor login on or off per student. Does not clear encrypted secrets, backup
            codes, or enrollment timestamps — toggling back on keeps existing authenticator apps
            working.
          </Text>
          {toggleUsersLoading ? (
            <ActivityIndicator style={{ marginTop: 12 }} color="#0176d3" />
          ) : (
            <View style={styles.twoFaUserList}>
              {toggleUsers.map((row) => {
                const required = Boolean(row.is_two_factor_user);
                const busy = toggleBusyId === row.id;
                const hidden =
                  row.is_hidden_student === true || row.isHiddenStudent === true;
                return (
                  <View
                    key={`toggle-${row.id}`}
                    style={[styles.twoFaUserRow, { borderColor: cardBorder }]}
                  >
                    <View style={styles.twoFaUserInfo}>
                      <Text style={[styles.twoFaUserName, { color: theme.colors.text }]}>
                        {row.student_name || row.username}
                      </Text>
                      <Text style={[styles.twoFaUserMeta, { color: theme.colors.text }]}>
                        {required ? '2FA required' : '2FA not required'}
                        {hidden ? ' · hidden student' : ''}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={[
                        styles.twoFaToggle,
                        required ? styles.twoFaToggleOn : styles.twoFaToggleOff,
                        busy && { opacity: 0.6 },
                      ]}
                      onPress={() => toggleTwoFactorRequired(row)}
                      disabled={busy || resetAllBusy || twoFaResettingId != null}
                      accessibilityRole="switch"
                      accessibilityState={{ checked: required }}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { userAuditTwoFaToggle: required ? 'on' : 'off' } }
                        : {})}
                    >
                      {busy ? (
                        <ActivityIndicator size="small" color="#fff" />
                      ) : (
                        <Text style={styles.twoFaToggleText}>{required ? 'On' : 'Off'}</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                );
              })}
              {!toggleUsers.length && !toggleUsersLoading ? (
                <Text style={[styles.twoFaEmpty, { color: theme.colors.text }]}>
                  No students found.
                </Text>
              ) : null}
            </View>
          )}
        </View>

        {!loading && !error && data && (
          <>
            <View style={[styles.sfBanner, { backgroundColor: bannerBg }]}>
              <Text style={styles.sfBannerTitle}>Login activity</Text>
              <Text style={styles.sfBannerSub}>
                Successful logins · Last 90 days (where noted)
              </Text>
            </View>

            <View style={styles.gridRow}>
              {renderWidget(
                'Logins this week (by user)',
                'Horizontal bars · successful sessions',
                <HorizontalBars
                  rows={data.loginsThisWeekByUser || []}
                  labelKey="student_name"
                  theme={theme}
                  isDark={isDark}
                />
              )}
              {renderWidget(
                'Device type',
                'Donut · successful logins (90 days)',
                <View style={styles.donutRow}>
                  <DonutVisual
                    segments={data.deviceTypeBreakdown || []}
                    theme={theme}
                    isDark={isDark}
                  />
                  <View style={styles.donutLegend}>
                    {(data.deviceTypeBreakdown || []).map((s, i) => {
                      const t =
                        (data.deviceTypeBreakdown || []).reduce(
                          (a, x) => a + (x.count || 0),
                          0
                        ) || 1;
                      return (
                        <View key={i} style={styles.legendRow}>
                          <View
                            style={[
                              styles.legendSwatch,
                              {
                                backgroundColor:
                                  CHART_COLORS[i % CHART_COLORS.length],
                              },
                            ]}
                          />
                          <Text
                            style={[styles.legendText, { color: theme.colors.text }]}
                          >
                            {s.device_type} · {s.count} (
                            {Math.round(((s.count || 0) / t) * 100)}%)
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </View>
              )}
              {renderWidget(
                'Latest session by user',
                'IP · device · OS · location',
                <DataTable
                  theme={theme}
                  isDark={isDark}
                  columns={[
                    { key: 'student_name', label: 'User', width: 120 },
                    { key: 'ip_address', label: 'IP address', width: 130 },
                    { key: 'device_type', label: 'Device', width: 90 },
                    { key: 'os', label: 'OS', width: 80 },
                    { key: 'location', label: 'Location', width: 160, multiline: true },
                  ]}
                  rows={data.latestByUser || []}
                />
              )}
            </View>

            <View style={styles.gridRow}>
              {renderWidget(
                'Login map',
                data.mapPins?.length
                  ? `Pins from recent successful logins (${Math.min(
                      data.mapPins.length,
                      18
                    )} shown on map)`
                  : 'No geo coordinates stored yet',
                data.mapPins?.length ? (
                  <LoginMapPanel pins={data.mapPins} height={280} isDark={isDark} />
                ) : (
                  <Text style={[styles.emptyHint, { color: theme.colors.text }]}>
                    Map appears when logins include latitude/longitude from geo-IP.
                  </Text>
                )
              )}
              {renderWidget(
                'Operating system',
                'Donut · successful logins (90 days)',
                <View style={styles.donutRow}>
                  <DonutVisual
                    segments={(data.osBreakdown || []).map((r) => ({
                      ...r,
                      device_type: r.os,
                    }))}
                    theme={theme}
                    isDark={isDark}
                  />
                  <View style={styles.donutLegend}>
                    {(data.osBreakdown || []).map((s, i) => {
                      const t =
                        (data.osBreakdown || []).reduce(
                          (a, x) => a + (x.count || 0),
                          0
                        ) || 1;
                      return (
                        <View key={i} style={styles.legendRow}>
                          <View
                            style={[
                              styles.legendSwatch,
                              {
                                backgroundColor:
                                  CHART_COLORS[i % CHART_COLORS.length],
                              },
                            ]}
                          />
                          <Text
                            style={[styles.legendText, { color: theme.colors.text }]}
                          >
                            {s.os} · {s.count} (
                            {Math.round(((s.count || 0) / t) * 100)}%)
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </View>
              )}
              {renderWidget(
                'Logins today',
                'Vertical bars · successful sessions',
                <VerticalBars
                  rows={data.loginsTodayByUser || []}
                  theme={theme}
                  isDark={isDark}
                />
              )}
            </View>

            <View style={[styles.sfBanner, { backgroundColor: '#5c4ee5', marginTop: 8 }]}>
              <Text style={styles.sfBannerTitle}>Suspicious Activities</Text>
              <Text style={styles.sfBannerSub}>
                New devices & locations · Flags · Failed sign-ins (90 days)
              </Text>
            </View>

            <View style={styles.gridRow}>
              {renderWidget(
                'New devices (90 days)',
                'Count of logins flagged as new_device',
                <View style={styles.metricBig}>
                  <Text style={[styles.metricNumber, { color: '#0176d3' }]}>
                    {data.suspicious?.newDeviceCount90d ?? 0}
                  </Text>
                  <Text style={[styles.metricCaption, { color: theme.colors.text }]}>
                    Events with new_device = true
                  </Text>
                </View>
              )}
              {renderWidget(
                'New locations (90 days)',
                'Recent first-seen location signals',
                <ScrollView style={{ maxHeight: 220 }}>
                  {(data.suspicious?.newLocations90d || []).length === 0 ? (
                    <Text style={[styles.emptyHint, { color: theme.colors.text }]}>
                      No new_location events in range.
                    </Text>
                  ) : (
                    (data.suspicious?.newLocations90d || []).map((row, i) => (
                      <View
                        key={i}
                        style={[
                          styles.listRow,
                          { borderBottomColor: isDark ? '#444' : '#e5e5e5' },
                        ]}
                      >
                        <Text style={[styles.listTitle, { color: theme.colors.heading }]}>
                          {row.location}
                        </Text>
                        <Text style={[styles.listMeta, { color: theme.colors.text }]}>
                          {row.student_name}
                          {row.at
                            ? ` · ${new Date(row.at).toLocaleString()}`
                            : ''}
                        </Text>
                      </View>
                    ))
                  )}
                </ScrollView>
              )}
              {renderWidget(
                'Suspicious logins',
                'user_name · is_suspicious (and context)',
                <DataTable
                  theme={theme}
                  isDark={isDark}
                  columns={[
                    { key: 'student_name', label: 'User', width: 110 },
                    { key: 'is_suspicious', label: 'is_suspicious', width: 100 },
                    { key: 'timestamp', label: 'When', width: 140 },
                    { key: 'ip_address', label: 'IP', width: 120 },
                    { key: 'new_device', label: 'new_device', width: 90 },
                    { key: 'new_location', label: 'new_location', width: 100 },
                  ]}
                  rows={(data.suspicious?.suspiciousLogins || []).map((r) => ({
                    ...r,
                    is_suspicious: String(r.is_suspicious),
                    new_device: String(r.new_device),
                    new_location: String(r.new_location),
                    timestamp: r.timestamp
                      ? new Date(r.timestamp).toLocaleString()
                      : '—',
                  }))}
                />
              )}
            </View>

            <View style={styles.gridRow}>
              {renderWidget(
                'Failure reasons (90 days)',
                'Summary counts',
                <DataTable
                  theme={theme}
                  isDark={isDark}
                  columns={[
                    { key: 'failure_reason', label: 'failure_reason', width: 160 },
                    { key: 'count', label: 'Count', width: 80 },
                  ]}
                  rows={data.suspicious?.failureReasonSummary || []}
                />
              )}
              {renderWidget(
                'Failed sign-ins (detail)',
                'Recent failed attempts · 90 days',
                <DataTable
                  theme={theme}
                  isDark={isDark}
                  columns={[
                    { key: 'student_name', label: 'User', width: 110 },
                    { key: 'failure_reason', label: 'failure_reason', width: 130 },
                    { key: 'timestamp', label: 'When', width: 140 },
                    { key: 'ip_address', label: 'IP', width: 120 },
                  ]}
                  rows={(data.suspicious?.failureReasonRecent || []).map((r) => ({
                    ...r,
                    student_name: r.student_name || '—',
                    timestamp: r.timestamp
                      ? new Date(r.timestamp).toLocaleString()
                      : '—',
                  }))}
                />
              )}
              {renderWidget(
                'Reporting notes',
                'Salesforce Event Monitoring style',
                <Text style={[styles.noteText, { color: theme.colors.text }]}>
                  This dashboard mirrors trusted-services reporting: component
                  cards, high-contrast metrics, and drill-friendly tables. Data
                  comes from the user_login stream (successful and failed
                  attempts). Login map uses OpenStreetMap tiles (Leaflet) when
                  coordinates exist on the login record.
                </Text>
              )}
            </View>
          </>
        )}
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 48,
    paddingBottom: 14,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#dddbda',
  },
  hamburgerContainer: { marginRight: 12 },
  breadcrumbContainer: { flex: 1 },
  breadcrumb: { fontSize: 20, fontWeight: '700' },
  breadcrumbSub: { fontSize: 12, marginTop: 2, opacity: 0.85 },
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 48, paddingHorizontal: 8 },
  refreshBtn: { alignSelf: 'flex-end', padding: 12 },
  refreshBtnText: { fontWeight: '600', fontSize: 14 },
  twoFaAdminCard: {
    marginHorizontal: 8,
    marginTop: 12,
    marginBottom: 8,
    padding: 14,
    borderRadius: 4,
    borderWidth: 1,
  },
  twoFaAdminTitle: { fontSize: 16, fontWeight: '700', marginBottom: 6 },
  twoFaAdminSub: { fontSize: 12, lineHeight: 17, marginBottom: 12, opacity: 0.9 },
  twoFaResetAllBtn: {
    alignSelf: 'flex-start',
    backgroundColor: '#ba0517',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 4,
    minWidth: 180,
    alignItems: 'center',
  },
  twoFaResetAllBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  twoFaUserList: { marginTop: 14 },
  twoFaUserRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  twoFaUserInfo: { flex: 1, marginRight: 12 },
  twoFaUserName: { fontSize: 14, fontWeight: '600', backgroundColor: 'transparent' },
  twoFaUserMeta: { fontSize: 11, marginTop: 2, opacity: 0.8, backgroundColor: 'transparent' },
  twoFaUserResetBtn: {
    backgroundColor: '#706e6b',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 4,
    minWidth: 72,
    alignItems: 'center',
  },
  twoFaUserResetBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  twoFaEmpty: { fontSize: 13, fontStyle: 'italic', marginTop: 8 },
  twoFaSectionDivider: {
    marginTop: 18,
    marginBottom: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  twoFaToggle: {
    minWidth: 56,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  twoFaToggleOn: {
    backgroundColor: '#2e844a',
  },
  twoFaToggleOff: {
    backgroundColor: '#ba0517',
  },
  twoFaToggleText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 13,
    backgroundColor: 'transparent',
  },
  errorBox: {
    margin: 12,
    padding: 12,
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: 'rgba(186,5,23,0.08)',
  },
  errorText: { color: '#ba0517', fontWeight: '600' },
  sfBanner: {
    marginHorizontal: 8,
    marginTop: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 4,
  },
  sfBannerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  sfBannerSub: { color: 'rgba(255,255,255,0.9)', fontSize: 12, marginTop: 4 },
  gridRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  widgetCard: {
    borderRadius: 4,
    borderWidth: 1,
    padding: 12,
    minHeight: 200,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.08,
          shadowRadius: 2,
          elevation: 2,
        }),
  },
  widgetHead: { marginBottom: 12, paddingBottom: 8, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#dddbda' },
  widgetTitle: { fontSize: 15, fontWeight: '700' },
  widgetSub: { fontSize: 11, marginTop: 4, opacity: 0.8 },
  emptyHint: { fontSize: 13, fontStyle: 'italic', paddingVertical: 8 },
  barBlock: { gap: 8 },
  hBarRow: { marginBottom: 10 },
  hBarLabel: { fontSize: 12, marginBottom: 4, fontWeight: '600' },
  hBarTrack: { height: 10, borderRadius: 4, overflow: 'hidden', flex: 1 },
  hBarFill: { height: '100%', borderRadius: 4 },
  hBarCount: { fontSize: 11, marginTop: 4, fontWeight: '700' },
  donutWrap: { alignItems: 'center', justifyContent: 'center' },
  donutRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
  donutLegend: { flex: 1, minWidth: 140 },
  donutFallback: { width: '100%' },
  legendRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  legendSwatch: { width: 10, height: 10, borderRadius: 2, marginRight: 8 },
  legendText: { fontSize: 11, flex: 1 },
  vChart: { paddingTop: 8 },
  vBarsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    minHeight: 140,
    gap: 8,
  },
  vCol: { width: 72, alignItems: 'center' },
  vBar: { width: 28, borderRadius: 4, marginBottom: 6 },
  vLabel: { fontSize: 9, textAlign: 'center' },
  vCount: { fontSize: 11, fontWeight: '700' },
  tr: { flexDirection: 'row', borderBottomWidth: StyleSheet.hairlineWidth, paddingVertical: 6 },
  trHead: { borderBottomWidth: 2, fontWeight: '700' },
  th: { fontSize: 11, fontWeight: '700', paddingHorizontal: 6 },
  td: { fontSize: 11, paddingHorizontal: 6 },
  metricBig: { alignItems: 'center', paddingVertical: 24 },
  metricNumber: { fontSize: 48, fontWeight: '800' },
  metricCaption: { fontSize: 12, marginTop: 8, textAlign: 'center' },
  listRow: { paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth },
  listTitle: { fontSize: 13, fontWeight: '600' },
  listMeta: { fontSize: 11, marginTop: 2 },
  noteText: { fontSize: 12, lineHeight: 18 },
});
